import React, { useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import StatCard from "../components/StatCard";
import { usePortfolio } from "../context/PortfolioContext";
import { useAuth } from "../context/AuthContext";
import { fmtINR, fmtUnits, fmtPct, longDate, shortDate } from "../lib/format";
import { computeMemberMetrics } from "../lib/xirr";
import { stampedName } from "../lib/csv";
import { exportMemberStatement } from "../lib/memberStatementPdf";
import ExportButtons from "../components/ExportButtons";
import { useToast } from "../hooks/use-toast";
import {
  ArrowLeft,
  Wallet,
  Layers,
  Percent,
  TrendingUp,
  Activity,
  Sparkles,
  FileDown,
  Plus,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { members, transactions, navHistory, summary, loading } = usePortfolio();
  const { toast } = useToast();

  const member = members.find((m) => m.id === id);
  const isAdmin = user?.role === "admin";
  const isMe = user?.memberId === id;

  const memberTxs = useMemo(
    () =>
      (transactions || [])
        .filter((t) => t.memberId === id)
        .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [transactions, id],
  );

  const metrics = useMemo(
    () => computeMemberMetrics(member, transactions),
    [member, transactions],
  );

  // Build member value-vs-capital timeline (replay txns + overlay NAV history)
  const timeline = useMemo(() => {
    if (!member || memberTxs.length === 0) return [];
    return buildMemberTimeline(memberTxs, navHistory);
  }, [member, memberTxs, navHistory]);

  const txCsv = useMemo(() => {
    const headers = ["Date", "Type", "NAV", "Units", "Amount", "Details"];
    const rows = memberTxs.map((t) => {
      const isBonus = t.type === "bonus";
      const isCr = !isBonus && (t.fundsCr || 0) > 0;
      const amount = isBonus ? t.fundsCr : isCr ? t.fundsCr : -(t.fundsDr || 0);
      const units = (t.unitsCr || 0) > 0 ? t.unitsCr : t.unitsDr || 0;
      return [
        t.date,
        isBonus ? "Bonus" : isCr ? "Subscription" : "Redemption",
        t.nav,
        units,
        amount,
        t.details || "",
      ];
    });
    return { headers, rows };
  }, [memberTxs]);

  const handleStatementPdf = () => {
    if (!member) return;
    try {
      exportMemberStatement({
        member,
        transactions: memberTxs,
        summary,
        metrics,
        periodLabel: summary?.lastNavDate
          ? `As of ${longDate(summary.lastNavDate)}`
          : `As of ${longDate(new Date().toISOString())}`,
        filename: stampedName(`statement_${(member.name || "").replace(/\s+/g, "_")}`).replace(
          /\.csv$/,
          "",
        ),
      });
      toast({
        title: "Statement generated",
        description: `${member.name} · ${memberTxs.length} transactions`,
      });
    } catch (e) {
      toast({
        title: "Statement failed",
        description: e?.message || "Could not generate PDF",
      });
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center py-24 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> Loading…
        </div>
      </AppShell>
    );
  }

  if (!member) {
    return (
      <AppShell>
        <div className="text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-md px-4 py-3 text-sm">
          Member not found.
          <Link
            to="/members"
            className="ml-2 text-emerald-400 hover:text-emerald-300 underline-offset-2 hover:underline"
          >
            Back to members
          </Link>
        </div>
      </AppShell>
    );
  }

  const positive = (member.gainLoss || 0) >= 0;
  const hasBonus = (member.bonusUnits || 0) > 0 || (member.bonusCapital || 0) > 0;
  const overallVal =
    member.overallCurrentValue != null ? member.overallCurrentValue : member.currentValue || 0;
  const overallGain =
    member.overallGainLoss != null ? member.overallGainLoss : member.gainLoss || 0;
  const overallPct =
    member.overallGainPct != null ? member.overallGainPct : member.gainLossPct || 0;
  const overallPositive = (overallGain || 0) >= 0;

  return (
    <AppShell>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button
            onClick={() => navigate("/members")}
            data-testid="member-detail-back"
            className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-emerald-300 font-mono uppercase tracking-[0.18em] mb-2 transition-colors"
          >
            <ArrowLeft size={11} /> Members
          </button>
          <div className="inline-flex items-center gap-2 text-[10px] text-emerald-300/85 uppercase tracking-[0.22em] font-mono">
            <span className="pulse-dot" style={{ width: 6, height: 6 }} />
            Member · {fmtPct(member.holdingPct || 0)} of pool
          </div>
          <h1 className="text-2xl md:text-4xl font-display text-white mt-1 tracking-tight">
            {member.name}
            {isMe && (
              <span className="ml-3 text-[11px] align-middle px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-mono uppercase tracking-wider">
                You
              </span>
            )}
          </h1>
          {metrics.firstDate && (
            <div className="mt-1 text-xs text-slate-400 font-mono">
              Member since {longDate(metrics.firstDate)} · {metrics.years || 0} yrs
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleStatementPdf}
            data-testid="member-statement-pdf-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
          >
            <FileDown size={14} className="mr-2" /> Download Statement
          </Button>
          <ExportButtons
            testIdPrefix="member-detail-export"
            csv={{
              filename: stampedName(
                `member_txns_${(member.name || "").replace(/\s+/g, "_")}`,
              ),
              ...txCsv,
            }}
            pdf={{
              title: `${member.name} — Transactions`,
              subtitle: summary?.lastNavDate ? `As of ${longDate(summary.lastNavDate)}` : undefined,
              filename: `member_txns_${(member.name || "").replace(/\s+/g, "_")}`,
              columns: ["Date", "Type", "NAV", "Units", "Amount", "Details"],
              rows: txCsv.rows.map((r) => [
                r[0],
                r[1],
                `INR ${Number(r[2] || 0).toFixed(4)}`,
                fmtUnits(r[3] || 0),
                fmtINR(r[4] || 0),
                String(r[5] || "").slice(0, 60),
              ]),
              summary: [
                ["Capital invested", fmtINR(member.capitalInvested || 0)],
                ["Capital units", fmtUnits(member.units || 0)],
                ["Current value", fmtINR(member.currentValue || 0)],
                ["Gain / Loss", `${fmtINR(member.gainLoss || 0)} (${fmtPct(member.gainLossPct || 0)})`],
                ["Holding %", fmtPct(member.holdingPct || 0)],
                ["XIRR", metrics.xirr != null ? fmtPct(metrics.xirr) : "—"],
                ["CAGR", metrics.cagr != null ? fmtPct(metrics.cagr) : "—"],
              ],
            }}
          />
          {isAdmin && (
            <Button
              onClick={() => navigate("/admin/add-transaction")}
              data-testid="member-add-tx-btn"
              className="bg-slate-800 hover:bg-slate-700 text-white border border-slate-700"
            >
              <Plus size={14} className="mr-2" /> Add transaction
            </Button>
          )}
        </div>
      </div>

      {/* Headline stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 md:gap-4">
        <StatCard
          testId="member-stat-capital"
          icon={Wallet}
          label="Capital Invested"
          value={fmtINR(member.capitalInvested || 0, { compact: true })}
          sub={`${fmtUnits(member.units || 0)} units`}
        />
        <StatCard
          testId="member-stat-current-value"
          icon={TrendingUp}
          label="Current Value"
          value={fmtINR(member.currentValue || 0, { compact: true })}
          sub={`@ NAV ₹${(summary?.currentNAV || 0).toFixed(4)}`}
        />
        <StatCard
          testId="member-stat-gain"
          icon={Percent}
          label="Gain / Loss"
          value={fmtINR(member.gainLoss || 0, { compact: true })}
          sub={fmtPct(member.gainLossPct || 0)}
          positive={positive}
          tone={positive ? "emerald" : "rose"}
          valueClassName={positive ? "text-emerald-400" : "text-rose-400"}
        />
        <StatCard
          testId="member-stat-holding"
          icon={Layers}
          label="Holding %"
          value={fmtPct(member.holdingPct || 0)}
          sub="of total pool"
        />
        <StatCard
          testId="member-stat-xirr"
          icon={Activity}
          label="XIRR"
          value={metrics.xirr != null ? fmtPct(metrics.xirr) : "—"}
          sub="money-weighted return"
          positive={metrics.xirr != null ? metrics.xirr >= 0 : undefined}
          tone={metrics.xirr != null && metrics.xirr < 0 ? "rose" : "emerald"}
          valueClassName={
            metrics.xirr == null
              ? "text-slate-500"
              : metrics.xirr >= 0
              ? "text-emerald-400"
              : "text-rose-400"
          }
        />
        <StatCard
          testId="member-stat-cagr"
          icon={TrendingUp}
          label="CAGR"
          value={metrics.cagr != null ? fmtPct(metrics.cagr) : "—"}
          sub={metrics.years ? `over ${metrics.years} yrs` : "—"}
          positive={metrics.cagr != null ? metrics.cagr >= 0 : undefined}
          tone={metrics.cagr != null && metrics.cagr < 0 ? "rose" : "emerald"}
          valueClassName={
            metrics.cagr == null
              ? "text-slate-500"
              : metrics.cagr >= 0
              ? "text-emerald-400"
              : "text-rose-400"
          }
        />
      </div>

      {/* Bonus + Overall (when applicable) */}
      {hasBonus && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div
            className="holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm rounded-xl p-5 print-card"
            data-testid="member-bonus-card"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] text-amber-300 uppercase tracking-widest flex items-center gap-2 font-mono">
                  <Sparkles size={11} /> Bonus Holdings
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono">From bonus units</div>
                <div className="mt-1 text-2xl md:text-3xl font-display text-white tabular-nums">
                  {fmtINR(member.bonusValue || 0)}
                </div>
              </div>
              <div
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border ${
                  (member.bonusGainPct || 0) >= 0
                    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                    : "text-rose-400 bg-rose-500/10 border-rose-500/30"
                }`}
              >
                {(member.bonusGainPct || 0) >= 0 ? (
                  <ArrowUpRight size={14} />
                ) : (
                  <ArrowDownRight size={14} />
                )}
                {fmtPct(member.bonusGainPct || 0)}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-amber-500/20 pt-3">
              <Mini label="Bonus Units" value={fmtUnits(member.bonusUnits || 0)} />
              <Mini label="Bonus Capital" value={fmtINR(member.bonusCapital || 0, { compact: true })} />
              <Mini label="Bonus Gain" value={fmtINR(member.bonusGainLoss || 0, { compact: true })} />
              <Mini
                label="Bonus NAV"
                value={
                  member.bonusUnits > 0
                    ? `₹${(member.bonusCapital / member.bonusUnits).toFixed(4)}`
                    : "—"
                }
              />
            </div>
          </div>

          <div
            className="holo-border scan-sheen bg-slate-900/60 backdrop-blur-sm border-2 border-emerald-500/40 rounded-xl p-5 print-card"
            data-testid="member-overall-card"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] text-emerald-300 uppercase tracking-widest font-mono">
                  Overall (Capital + Bonus)
                </div>
                <div className="text-xs text-slate-400 mt-1 font-mono">Total current value</div>
                <div className="mt-1 text-2xl md:text-3xl font-display text-white tabular-nums">
                  {fmtINR(overallVal)}
                </div>
              </div>
              <div
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border ${
                  overallPositive
                    ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                    : "text-rose-400 bg-rose-500/10 border-rose-500/30"
                }`}
              >
                {overallPositive ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {fmtPct(overallPct || 0)}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-800 pt-3">
              <Mini label="Total Units" value={fmtUnits(member.overallUnits || member.units || 0)} />
              <Mini label="Overall Gain" value={fmtINR(overallGain || 0, { compact: true })} />
              <Mini label="Capital Value" value={fmtINR(member.currentValue || 0, { compact: true })} />
              <Mini label="Bonus Value" value={fmtINR(member.bonusValue || 0, { compact: true })} />
            </div>
          </div>
        </div>
      )}

      {/* Value vs Capital chart */}
      {timeline.length > 1 && (
        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl p-4 md:p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm font-semibold text-white">Value vs Capital Invested</div>
              <div className="text-xs text-slate-500 font-mono">
                {timeline.length} data points · {shortDate(timeline[0].date)} →{" "}
                {shortDate(timeline[timeline.length - 1].date)}
              </div>
            </div>
            <div className="flex items-center gap-3 text-[11px] font-mono">
              <span className="inline-flex items-center gap-1.5 text-emerald-400">
                <span className="w-3 h-0.5 bg-emerald-400 rounded" /> Current Value
              </span>
              <span className="inline-flex items-center gap-1.5 text-slate-400">
                <span
                  className="w-3 h-0.5 rounded"
                  style={{ borderTop: "1.5px dashed #64748b" }}
                />{" "}
                Capital Invested
              </span>
            </div>
          </div>
          <div className="mt-4 h-64 md:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  stroke="#475569"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={40}
                />
                <YAxis
                  stroke="#475569"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => fmtINR(v, { compact: true, showSymbol: false })}
                  width={55}
                />
                <Tooltip
                  contentStyle={{
                    background: "#0f172a",
                    border: "1px solid #1e293b",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "#e2e8f0",
                  }}
                  formatter={(v, n) => [
                    fmtINR(v),
                    n === "value" ? "Current Value" : "Capital Invested",
                  ]}
                  labelFormatter={shortDate}
                />
                <Line
                  type="monotone"
                  dataKey="capital"
                  stroke="#64748b"
                  strokeWidth={1.5}
                  strokeDasharray="4 3"
                  dot={false}
                  name="capital"
                />
                <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} name="value" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Transactions table */}
      <div className="mt-6 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-800">
          <div>
            <div className="text-sm font-semibold text-white">Transactions</div>
            <div className="text-xs text-slate-500 font-mono">
              {memberTxs.length} record{memberTxs.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {memberTxs.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-slate-500">
            No transactions yet for this member.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80 text-[11px] text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Type</th>
                  <th className="text-right px-5 py-3">NAV</th>
                  <th className="text-right px-5 py-3">Units</th>
                  <th className="text-right px-5 py-3">Amount</th>
                  <th className="text-left px-5 py-3">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {memberTxs.map((t) => {
                  const isBonus = t.type === "bonus";
                  const isCr = !isBonus && (t.fundsCr || 0) > 0;
                  const amount = isBonus ? t.fundsCr : isCr ? t.fundsCr : -(t.fundsDr || 0);
                  const units = (t.unitsCr || 0) > 0 ? t.unitsCr : t.unitsDr || 0;
                  const color = isBonus
                    ? "text-amber-400"
                    : isCr
                    ? "text-emerald-400"
                    : "text-rose-400";
                  const label = isBonus ? "Bonus" : isCr ? "Subscription" : "Redemption";
                  return (
                    <tr
                      key={t.id || `${t.date}-${t.memberId}`}
                      className="hover:bg-slate-800/40 transition-colors"
                      data-testid={`member-tx-row-${t.id || t.date}`}
                    >
                      <td className="px-5 py-3 text-slate-300 whitespace-nowrap font-mono text-xs">
                        {longDate(t.date)}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${
                            isBonus
                              ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                              : isCr
                              ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                              : "bg-rose-500/10 text-rose-300 border-rose-500/30"
                          }`}
                        >
                          {label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-slate-300 text-xs">
                        ₹{Number(t.nav || 0).toFixed(4)}
                      </td>
                      <td className="px-5 py-3 text-right font-mono text-slate-300 text-xs">
                        {fmtUnits(units)}
                      </td>
                      <td className={`px-5 py-3 text-right font-medium ${color}`}>
                        {isCr || isBonus ? "+" : ""}
                        {fmtINR(amount)}
                      </td>
                      <td className="px-5 py-3 text-slate-400 text-xs max-w-[260px] truncate">
                        {t.details || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">{label}</div>
      <div className="mt-1 text-sm font-display text-white tabular-nums">{value}</div>
    </div>
  );
}

// Replay member transactions against the pool NAV history to build a
// (date, current-value, capital-invested) timeline for the chart.
function buildMemberTimeline(memberTxs, navHistory) {
  const asc = [...memberTxs].sort((a, b) => new Date(a.date) - new Date(b.date));

  const events = [];
  let cumUnits = 0;
  let cumCapital = 0;
  for (const t of asc) {
    const isBonus = t.type === "bonus";
    const isSub = !isBonus && (t.fundsCr || 0) > 0;
    if (isSub) {
      cumUnits += t.unitsCr || 0;
      cumCapital += t.fundsCr || 0;
    } else if (!isBonus) {
      cumUnits = Math.max(0, cumUnits - (t.unitsDr || 0));
      cumCapital = Math.max(0, cumCapital - (t.fundsDr || 0));
    } else {
      cumUnits += t.unitsCr || 0;
    }
    events.push({ date: t.date, units: cumUnits, capital: cumCapital, nav: t.nav });
  }

  const navMap = {};
  for (const n of navHistory || []) navMap[n.date] = n.nav;
  const allDates = new Set([
    ...events.map((e) => e.date),
    ...(navHistory || []).map((n) => n.date),
  ]);
  const sortedDates = [...allDates].sort();

  const out = [];
  let runUnits = 0;
  let runCapital = 0;
  let eIdx = 0;
  for (const d of sortedDates) {
    while (eIdx < events.length && events[eIdx].date <= d) {
      runUnits = events[eIdx].units;
      runCapital = events[eIdx].capital;
      eIdx++;
    }
    if (runUnits <= 0 && runCapital <= 0) continue;
    const nav = navMap[d] || (eIdx > 0 ? events[eIdx - 1].nav : 0);
    out.push({
      date: d,
      value: Math.round(runUnits * nav * 100) / 100,
      capital: Math.round(runCapital * 100) / 100,
    });
  }
  return out;
}
